/* eslint-disable no-loop-func */
import React, { PureComponent } from "react";
import styled from 'styled-components';
import { Loader } from 'react-loaders';
import moment from "moment";

import ErrorAlert from '../../components/alerts/error';
import DecisionsAlert from '../../components/alerts/decisions';
import Auth from "../../routes/Auth";
import { extractRoundErrorMessage } from "../utils/round-error";

import {
  getRequest,
  putRequest,
  postRequest,
  patchRequest
} from "../../api/ApiService";
import { fetchAppGeneralInfo, clearAppGeneralInfoCache } from "../../api/AppGeneralInfoService";

import RoundAdminTable from '../components/round-admin/table';
import FirstDecisionModal from '../../components/first-decision-modal';

const Wrapper = styled.div`
  width: 100%;
  padding: 40px 40px 50px;
  background: #FAFAFA;

  .loader {
    display: inline-block;
    margin-top: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

const LoaderWrapper = styled.div`
  width: 100%;
  padding: 40px 40px 50px;
`;

const PageTitle = styled.h1`
  font-weight: 700;
  font-size: 21px;
  line-height: 28px;
  font-family: 'Mukta Vaani';
  color: rgba(0, 27, 59, 0.94);
`;

/* Round Activation */

const ActivationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;
`;

const ActivationHint = styled.span`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

const ActivationButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  border-radius: 4px;
  border: 1px solid rgba(0, 27, 59, 0.94);
  font-family: 'Mukta Vaani';
  font-size: 16px;
  line-height: 22px;
  cursor: ${p => p.isClickable ? 'pointer' : 'default'};
  padding: 10px 24px;
  background: #fff;
  color: rgba(0, 27, 59, 0.94);
  font-weight: 500;

  ${p => p.isClickable && `
    background: #22C55E;
    border-color: #22C55E;
    color: #fff;
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);

    && .prefix { color: rgba(255, 255, 255, 0.9); }
    && .status { color: #fff; }

    &:hover {
      background: #16A34A;
      border-color: #16A34A;
    }
  `}

  ${p => !p.isClickable && `
    opacity: 0.6;
  `}
`;

const ActivationButtonText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 15px;
  line-height: 22px;

  .prefix {
    color: rgba(0, 27, 59, 0.6);
    font-weight: 400;
  }
  .status {
    color: rgba(0, 27, 59, 0.94);
    font-weight: 700;
  }
`;

/* Round Deadlines Card */

const CardWrapper = styled.div`
  background: #fff;
  border: 1px solid rgba(0, 27, 59, 0.94);
  border-radius: 8px;
  padding: 19px 20px;
  margin-top: 20px;
  max-width: 880px;
`;

const CardTitle = styled.h3`
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  font-family: 'Inter', sans-serif;
  color: rgba(0, 27, 59, 0.94);
  text-align: center;
  margin: 0 0 8px;
`;

const CardSubtitle = styled.p`
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  text-align: center;
  margin: 0 0 21px;
`;

const CardActivationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0 10px;
`;

const ActivationTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 100ms ease;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }
`;

const ActivationHoverGroup = styled.div`
  position: relative;
  display: inline-flex;

  &:hover ${ActivationTooltip} {
    opacity: 1;
  }
`;

const CardDivider = styled.hr`
  border: none;
  border-top: 1px solid #D5D9DD;
  margin: 0;
`;

/* Confirmation Modal */

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConfirmModal = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
  padding: 32px;
  max-width: 480px;
  width: 90%;
  font-family: 'Mukta Vaani';
`;

const ConfirmTitle = styled.h3`
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 16px;
`;

const ConfirmMessage = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.8);
  margin: 0 0 24px;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ConfirmCancelButton = styled.button`
  background: #F5F6F7;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  padding: 8px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.8);
  cursor: pointer;
`;

const ConfirmProceedButton = styled.button`
  background: #2196F3;
  border: none;
  border-radius: 4px;
  padding: 8px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 14px;
  color: #fff;
  cursor: pointer;

  &:hover {
    background: #1e88e5;
  }

  &:disabled {
    background: #90CAF9;
    cursor: not-allowed;
  }
`;

class RoundAdmin extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      gameId: 0,
      gameType: null,
      courseId: 0,
      gameReset: false,
      rounds: [],
      schedulers: [],
      editedDates: {},
      editedGradingWeights: {},
      changedRounds: [],
      defaultActiveRounds: [],
      showSuccess: false,
      successfullyChangedRoundNumber: 0,
      showError: false,
      errorTitle: '',
      errorMessage: '',
      startDateChanged: false,
      endDateChanged: false,
      loadingButtonRound: 0,
      roundsName: '',
      showConfirmModal: false,
      activationLoading: false,
      // Coaching popup fired after a round is created (not edited).
      // Message varies by game type, gameReset, and which round was
      // created. See notifyRoundCreated below for the full matrix.
      roundsSetPopup: { open: false, title: '', body: '' },
    };
  }

  openRoundsSetPopup = (title, body) => {
    this.setState({ roundsSetPopup: { open: true, title, body } });
  }

  closeRoundsSetPopup = () => {
    this.setState({ roundsSetPopup: { open: false, title: '', body: '' } });
  }

  // Called from each round-creation success handler. Decides which
  // popup (if any) fires, based on:
  //   - gameType (Adventurer / Explorer / Legend)
  //   - gameReset flag at the time of creation
  //   - which round was just created (practice or main; number)
  //   - the resulting state of rounds/schedulers so we can detect
  //     "all rounds are now set"
  //
  // Skipped intentionally:
  //   - Edits to existing rounds/schedulers (PATCH/PUT paths)
  //   - Auto-creation during the Activate Main Rounds button flow
  //     (that lives in createMainRounds, which never calls this)
  notifyRoundCreated = ({
    gameType,
    gameReset,
    isPractice,
    createdRoundNumber,
    newRounds,
    newSchedulers,
  }) => {
    const isLegend = gameType === 'Legend';

    if (isPractice) {
      // Practice round can only exist on Explorer/Adventurer.
      if (gameType === 'Explorer') {
        this.openRoundsSetPopup(
          'Practice round is all set!',
          "Next up, set Round 1's deadline as well."
        );
      } else if (gameType === 'Adventurer') {
        this.openRoundsSetPopup(
          'Practice round is all set!',
          'Make sure to also set the deadlines for Round 1, 2, and 3.'
        );
      }
      return;
    }

    // Main round created directly via round API (Legend, or
    // Explorer/Adventurer post-reset).
    if (isLegend) {
      const mainRoundsCount = (newRounds || [])
        .filter(r => r && r.practice !== 'Yes')
        .length;
      if (Number(createdRoundNumber) === 1) {
        this.openRoundsSetPopup(
          'Round 1 is all set!',
          'Next up, set the deadlines for Round 2 and 3.'
        );
      } else if (mainRoundsCount >= 3) {
        this.openRoundsSetPopup('Rounds are all set!', '');
      }
      return;
    }

    if (gameReset) {
      // Post-reset Explorer/Adventurer main rounds. User only wants
      // a popup on Round 1 in this scenario.
      if (Number(createdRoundNumber) !== 1) return;
      if (gameType === 'Explorer') {
        this.openRoundsSetPopup('Round is all set!', '');
      } else if (gameType === 'Adventurer') {
        this.openRoundsSetPopup(
          'Round 1 is all set!',
          'Next up, set the deadlines for Round 2 and 3.'
        );
      }
      return;
    }

    // Pre-reset Explorer/Adventurer main rounds go through the
    // scheduler API. Popup fires when the FULL set is now present.
    const requiredNumbers = gameType === 'Explorer' ? [1] : [1, 2, 3];
    const hasAll = requiredNumbers.every(n =>
      (newSchedulers || []).some(s => Number(s.number) === n)
    );
    if (hasAll) {
      this.openRoundsSetPopup(
        'Rounds are all set!',
        'When the practice round ends, click Activate Main Rounds below to go live for the Main Rounds.'
      );
    }
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (this.state.gameId === 0 && this.props.game && this.props.game.id) {
      this.setState({ gameId: this.props.game.id });
    }
  }

  fetchData = () => {
    const gameId = Auth.getGameId();

    // Use the cached / in-flight-dedup'd fetcher so this page shares
    // one network request with App.js's own general-info call on
    // page load (and with the onboarding gate). Was previously a
    // raw getRequest that bypassed the cache and burned an extra
    // roundtrip on every mount.
    fetchAppGeneralInfo(gameId, 'instructor')
      .then(data => {
        // Pick the course / game that MATCH the current session,
        // not blindly [0]. When an instructor is tied to multiple
        // courses/games, general-info returns all of them and [0]
        // may be a stale/foreign course whose game_type is null,
        // which would leave gameType null and collapse the round
        // list to a single row.
        const currentCourseId = Number(Auth.getCourseId());
        const currentGameId = Number(Auth.getGameId());
        const coursesList = (data.courses || []);
        const gamesList = (data.games || []);
        const course = coursesList.find(c => Number(c.id) === currentCourseId)
          || coursesList[0]
          || {};
        const rawGameType = course.game_type || null;
        const gameType = rawGameType
          ? rawGameType.charAt(0).toUpperCase() + rawGameType.slice(1).toLowerCase()
          : null;
        const courseId = course.id || 0;

        const game = gamesList.find(g => Number(g.id) === currentGameId)
          || gamesList[0]
          || {};
        const propsGameId = game.id || (this.props.game && this.props.game.id ? this.props.game.id : 0);
        const gameReset = !!game.reset;

        const apiRounds = (data.rounds || []).map(i => ({
          id: i.id,
          number: i.number,
          start_date: moment(i.start_date),
          end_date: moment(i.end_date),
          showResults: i.results_viewable,
          is_started: i.is_started,
          is_finished: i.is_finished,
          practice: i.practice,
          grading_weight: i.grading_weight != null ? Number(i.grading_weight) : null,
          source: 'round_api',
        }));

        const roundsName = game.name || 'Practice Game';

        // Now fetch round schedulers
        getRequest(`${process.env.REACT_APP_API_URL}v2/games/roundschedulers/`)
          .then(schedRes => {
            const schedulers = (schedRes.body.results || schedRes.body || []).map(s => ({
              id: s.id,
              number: s.round_number !== undefined ? s.round_number : s.number,
              start_date: s.start_date ? moment(s.start_date) : null,
              end_date: s.end_date ? moment(s.end_date) : null,
              showResults: false,
              is_started: false,
              is_finished: false,
              practice: s.practice === true,
              grading_weight: s.grading_weight != null ? Number(s.grading_weight) : null,
              source: 'scheduler',
              scheduler_id: s.id,
            }));

            this.setState({
              loaded: true,
              gameId: propsGameId,
              gameType,
              courseId,
              gameReset,
              rounds: apiRounds,
              schedulers,
              roundsName,
            });
          })
          .catch(err => {
            // Schedulers failed, proceed without them
            this.setState({
              loaded: true,
              gameId: propsGameId,
              gameType,
              courseId,
              gameReset,
              rounds: apiRounds,
              schedulers: [],
              roundsName,
            });
          });
      })
      .catch(err => {
        this.fetchRoundsLegacy();
      });
  }

  // Rule: a main round's start_date cannot be more than 30 days after
  // the prior main round's end_date. Practice rounds don't count as
  // a prior. Skips when either the round being checked or its prior
  // has no start/end set yet, or when there is no prior main round
  // (i.e. Round 1). Returns { priorDisplayName, maxStart, priorEndDate }
  // on violation, or null.
  getStartGapViolation = (round, displayRounds) => {
    if (!round) return null;
    // Practice rounds (number = 0 in our display, or displayName = Practice)
    // are excluded from this rule entirely.
    if (round.displayName === 'Practice') return null;
    if (Number(round.number) <= 1) return null;
    if (!round.start_date) return null;

    const prior = (displayRounds || []).find(
      r => r.displayName !== 'Practice' && Number(r.number) === Number(round.number) - 1
    );
    if (!prior || !prior.end_date) return null;

    const priorEnd = moment.isMoment(prior.end_date) ? prior.end_date : moment(prior.end_date);
    const start = moment.isMoment(round.start_date) ? round.start_date : moment(round.start_date);
    const maxStart = priorEnd.clone().add(30, 'days');
    if (start.isAfter(maxStart)) {
      return {
        priorDisplayName: prior.displayName || `Round ${prior.number}`,
        maxStart,
        priorEndDate: priorEnd,
      };
    }
    return null;
  }

  getDisplayRounds = () => {
    const { rounds, schedulers, gameType, gameReset } = this.state;
    const displayRounds = [];
    const hasPractice = gameType === 'Explorer' || gameType === 'Adventurer';
    const mainRoundCount = (gameType === 'Adventurer' || gameType === 'Legend') ? 3 : 1;

    // Separate practice and main rounds from the round API by the
    // `practice` field. We always set this field explicitly on creation
    // (Yes for the practice round, No for main rounds), so it's the
    // source of truth for classifying rounds regardless of whether any
    // schedulers exist.
    const practiceApiRounds = rounds.filter(r => r.practice === 'Yes');
    const mainApiRounds = rounds
      .filter(r => r.practice !== 'Yes')
      .sort((a, b) => a.number - b.number);

    // Practice round row
    if (hasPractice) {
      if (!gameReset) {
        // reset=false: prefer round API (practice='Yes'), fall back to a
        // scheduler where practice === true.
        if (practiceApiRounds.length > 0) {
          displayRounds.push({ ...practiceApiRounds[0], displayName: 'Practice' });
        } else {
          const practiceScheduler = schedulers.find(s => s.practice === true);
          if (practiceScheduler && practiceScheduler.start_date && practiceScheduler.end_date) {
            displayRounds.push({ ...practiceScheduler, displayName: 'Practice', number: 0 });
          } else {
            displayRounds.push(this.makePlaceholder('Practice', 0));
          }
        }
      } else {
        // reset=true: check scheduler where practice === true, mark as disabled
        const practiceScheduler = schedulers.find(s => s.practice === true);
        if (practiceScheduler && practiceScheduler.start_date && practiceScheduler.end_date) {
          displayRounds.push({ ...practiceScheduler, displayName: 'Practice', number: 0, disabled: true });
        } else {
          displayRounds.push({ ...this.makePlaceholder('Practice', 0), disabled: true });
        }
      }
    }

    // Main round rows
    for (let i = 0; i < mainRoundCount; i++) {
      const roundNumber = i + 1;

      if (!gameReset) {
        // reset=false: check scheduler for a matching main-round entry
        // (practice !== true, so a practice scheduler with the same number
        // is not accidentally picked). For Round 1, this covers the case
        // where a scheduler with practice=false represents the main run.
        const scheduler = schedulers.find(s => s.number === roundNumber && s.practice !== true);
        if (scheduler && scheduler.start_date && scheduler.end_date) {
          displayRounds.push({ ...scheduler, displayName: `Round ${roundNumber}`, number: roundNumber });
        } else {
          displayRounds.push(this.makePlaceholder(`Round ${roundNumber}`, roundNumber));
        }
      } else {
        // reset=true: check general-info for round id first, then scheduler
        // (with practice !== true so we don't accidentally pick a practice
        // scheduler that happens to share the same round number).
        const apiRound = mainApiRounds.find(r => r.number === roundNumber);
        if (apiRound) {
          displayRounds.push({ ...apiRound, displayName: `Round ${roundNumber}` });
        } else {
          const scheduler = schedulers.find(s => s.number === roundNumber && s.practice !== true);
          if (scheduler && scheduler.start_date && scheduler.end_date) {
            displayRounds.push({ ...scheduler, displayName: `Round ${roundNumber}`, number: roundNumber });
          } else {
            displayRounds.push(this.makePlaceholder(`Round ${roundNumber}`, roundNumber));
          }
        }
      }
    }

    // Merge edited dates + grading weights into display rounds.
    //
    // Grading weight falls through the same source chain as dates:
    // round API record → scheduler record → placeholder default
    // (0.3333). `getDisplayRounds` already picked the row from this
    // chain, so we just read `r.grading_weight` here regardless of
    // which source backed it, and fall back to 0.3333 when neither
    // round API nor scheduler carried a value.
    const { editedDates, editedGradingWeights } = this.state;
    const merged = displayRounds.map(r => {
      const dateEdits = editedDates[r.id];
      const editedGW = editedGradingWeights[r.id];
      const storedGW = r.grading_weight != null ? Number(r.grading_weight) : null;
      const gradingWeight = editedGW !== undefined
        ? editedGW
        : (storedGW != null ? storedGW : 0.3333);
      const withDates = dateEdits
        ? {
            ...r,
            start_date: dateEdits.start_date || r.start_date,
            end_date: dateEdits.end_date || r.end_date,
          }
        : r;

      // Show Results toggle: enabled only when a matching round API
      // record exists (practice row needs number=1 + practice='Yes';
      // main rows need matching number + practice='No').
      const isPractice = r.displayName === 'Practice';
      const targetNumber = isPractice ? 1 : Number(r.number);
      const targetPractice = isPractice ? 'Yes' : 'No';
      const hasRoundRecord = rounds.some(rec =>
        Number(rec.number) === targetNumber && rec.practice === targetPractice
      );
      const showResultsDisabled = !hasRoundRecord;
      let showResultsTooltip = null;
      if (showResultsDisabled) {
        if (isPractice) {
          showResultsTooltip = 'Set up the practice round before toggling this.';
        } else if (!gameReset) {
          showResultsTooltip = 'The game is still in the practice round. Show Results can be adjusted once the main rounds are live.';
        } else {
          showResultsTooltip = 'Set up this round before toggling.';
        }
      }

      return {
        ...withDates,
        grading_weight: gradingWeight,
        showResultsDisabled,
        showResultsTooltip,
      };
    });

    // Second pass: attach a startGapWarning string when a main round's
    // start_date is more than 30 days past the prior main round's
    // end_date. Uses `merged` so edits-in-progress on the prior round
    // are reflected. Rendered inline under the start-date picker in
    // RoundAdminTable; onUpdate also blocks save on the same rule.
    return merged.map(r => {
      const violation = this.getStartGapViolation(r, merged);
      if (!violation) return r;
      return {
        ...r,
        startGapWarning: `Start date must be within 30 days of ${violation.priorDisplayName}'s end date (by ${violation.maxStart.format('MMM D, YYYY')}).`,
      };
    });
  }

  makePlaceholder = (displayName, number) => ({
    id: `placeholder-${number}`,
    number,
    start_date: null,
    end_date: null,
    showResults: false,
    is_started: false,
    is_finished: false,
    displayName,
    source: 'placeholder',
  })

  fetchRoundsLegacy = () => {
    getRequest(
      `${process.env.REACT_APP_API_URL}game/rounds`
    )
      .then(res => {
        let rounds = [];
        let showDefaultRounds = false;

        const propsGameId = this.props.game && this.props.game.id ? this.props.game.id : 0;

        if (res.body.length) {
          this.setState({ gameId: res.body[0].game || propsGameId });
          rounds = res.body
            .map(i => ({
              id: i.id,
              number: i.number,
              start_date: moment(i.start_date),
              end_date: moment(i.end_date),
              showResults: i.results_viewable,
              is_started: i.is_started || false,
              is_finished: i.is_finished || false,
              source: 'round_api',
            }));
        } else {
          rounds = Array.from({ length: 3 }, (_, i) => ({
            id: i + 1,
            number: i + 1,
            start_date: moment().add(7 * i + 14 * i, 'days'),
            end_date: moment().add(7 * i + 14 * (i + 1), 'days'),
            showResults: true,
            is_started: false,
            is_finished: false,
            source: 'placeholder',
          }));

          showDefaultRounds = true;
          this.setState({ gameId: propsGameId });
        }

        const roundWithLowestID = rounds.reduce((previous, current) => {
          return current.id < previous.id ? current : previous;
        });

        if (!showDefaultRounds) {
          getRequest(
            `${process.env.REACT_APP_API_URL}game/rounds/${roundWithLowestID.id}`
          )
            .then(res => {
              const roundsName = res.body.name ? res.body.name : 'Practice Game';

              this.setState({
                loaded: true,
                rounds,
                roundsName
              });
            })
            .catch(err => { });
        } else {
          const roundsName = 'Practice Game';
          this.setState({
            loaded: true,
            rounds,
            roundsName,
            defaultActiveRounds: [1, 2, 3]
          });
        }
      })
      .catch(err => { });
  }

  onChange = (datetime, round_id, field) => {
    if (typeof datetime === "string") {
      return;
    }

    let startDateChanged = false;
    let endDateChanged = false;

    if (field === 'start_date') {
      startDateChanged = true;
    } else if (field === 'end_date') {
      endDateChanged = true;
    }

    const { changedRounds, editedDates } = this.state;

    // Store edited dates keyed by round_id. The persisted `rounds` /
    // `schedulers` arrays are intentionally NOT mutated here — the
    // display path overlays `editedDates` on top (see getDisplayRounds),
    // and downstream checks that depend on the persisted server value
    // (e.g. the Activate Main Rounds gating on the practice round's
    // end_date) must not see un-saved picker changes.
    const existingEdits = editedDates[round_id] || {};
    const newEditedDates = {
      ...editedDates,
      [round_id]: { ...existingEdits, [field]: datetime },
    };

    let newChangedRounds = [...changedRounds];
    if (!newChangedRounds.includes(round_id)) {
      newChangedRounds.push(round_id);
    }

    this.setState({
      editedDates: newEditedDates,
      changedRounds: newChangedRounds,
      startDateChanged,
      endDateChanged
    });
  };

  onGradingWeightChange = (round_id, value) => {
    const { changedRounds, editedGradingWeights } = this.state;
    const next = { ...editedGradingWeights, [round_id]: value };
    const nextChanged = changedRounds.includes(round_id)
      ? [...changedRounds]
      : [...changedRounds, round_id];
    this.setState({
      editedGradingWeights: next,
      changedRounds: nextChanged,
    });
  };

  onUpdate = round_id => {
    // Find the round in display rounds
    const displayRounds = this.getDisplayRounds();
    const round = displayRounds.find(r => r.id === round_id);
    if (!round) return;

    const {
      startDateChanged,
      endDateChanged,
    } = this.state;

    if (!round.start_date || !round.end_date) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Missing Dates',
        errorMessage: 'Please set both start and end dates before updating.',
      });
      return;
    }

    const startDate = moment.isMoment(round.start_date) ? round.start_date : moment(round.start_date);
    const endDate = moment.isMoment(round.end_date) ? round.end_date : moment(round.end_date);
    const currentDateTime = moment();

    if (endDate.isBefore(startDate)) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Start Time > End Time',
        errorMessage: 'The start date/time cannot be greater than the end date/time.',
      });
      return;
    }

    const otherRounds = displayRounds.filter(r => r.id !== round_id && r.start_date && r.end_date);
    const overlappingRound = otherRounds.find(r => {
      const otherStart = moment.isMoment(r.start_date) ? r.start_date : moment(r.start_date);
      const otherEnd = moment.isMoment(r.end_date) ? r.end_date : moment(r.end_date);
      return startDate.isBefore(otherEnd) && endDate.isAfter(otherStart);
    });

    if (overlappingRound) {
      const overlapName = overlappingRound.displayName || `Round ${overlappingRound.number}`;
      this.setState({
        showError: true,
        errorTitle: 'Error | Overlapping Rounds',
        errorMessage: `The dates/times for ${round.displayName || 'this round'} overlap with ${overlapName}. Please adjust the dates so that rounds do not overlap.`,
      });
      return;
    }

    // Compare at minute precision so an instructor who picks a time
     // equal to "right now" (e.g. 11:00 while the clock just ticked
     // past 11:00:25) isn't flagged for being in the past. The intent
     // of the rule is to block earlier calendar/clock-minute values,
     // not sub-minute slippage.
    if (
      (startDateChanged && startDate.isBefore(currentDateTime, 'minute'))
      || (endDateChanged && endDate.isBefore(currentDateTime, 'minute'))
    ) {
      this.setState({
        showError: true,
        errorTitle: 'Error | In the Past',
        errorMessage: 'The start or end date/time cannot be set in the past.',
      });
      return;
    }

    if (Math.abs(startDate.diff(endDate, 'days')) > 30) {
      this.setState({
        showError: true,
        errorTitle: 'Error | 30 Days Restriction',
        errorMessage: 'A round cannot be set 30 days or more.',
      });
      return;
    }

    // 30-day gap rule: a main round's start_date cannot be more than
    // 30 days past the prior main round's end_date. Practice rounds
    // are skipped. See getStartGapViolation for the exact conditions.
    const gapViolation = this.getStartGapViolation(round, displayRounds);
    if (gapViolation) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Round Gap Too Large',
        errorMessage: `${round.displayName || 'This round'}'s start date must be within 30 days of ${gapViolation.priorDisplayName}'s end date (by ${gapViolation.maxStart.format('MMM D, YYYY')}).`,
      });
      return;
    }

    // Round weight: validate the range for non-practice rows. Actual
    // payload resolution happens below (once `rounds` is destructured
    // from state) so we can compare against the stored value.
    const editedGWRaw = this.state.editedGradingWeights[round_id];
    if (round.displayName !== 'Practice' && editedGWRaw !== undefined) {
      const editedGWNum = Number(editedGWRaw);
      if (!Number.isFinite(editedGWNum) || editedGWNum <= 0 || editedGWNum > 1) {
        this.setState({
          showError: true,
          errorTitle: 'Error | Invalid Round Weight',
          errorMessage: 'Round weight must be greater than 0 and no more than 1.',
        });
        return;
      }
    }

    this.setState({ loadingButtonRound: round_id });

    const { changedRounds, gameId, gameType, gameReset, rounds, schedulers } = this.state;
    const apiUrl = process.env.REACT_APP_API_URL;
    const isLegend = gameType === 'Legend';
    const isPractice = round.displayName === 'Practice';

    // Resolve the grading_weight value to include in the outgoing body.
    // - Practice: always inherits Round 1's current grading_weight.
    // - Main rounds: include only if the edited value differs from the
    //   backend's stored value (skip no-op writes).
    let gradingWeightPayload; // undefined = omit from body
    if (isPractice) {
      const round1 = displayRounds.find(r =>
        r.displayName !== 'Practice' && Number(r.number) === 1
      );
      if (round1 && round1.grading_weight != null) {
        const gw = Number(round1.grading_weight);
        if (Number.isFinite(gw) && gw > 0 && gw <= 1) {
          gradingWeightPayload = gw;
        }
      }
    } else if (editedGWRaw !== undefined) {
      const editedGWNum = Number(editedGWRaw);
      const apiRoundRecord = rounds.find(r => r.id === round_id && r.source === 'round_api');
      const storedGW = apiRoundRecord && apiRoundRecord.grading_weight != null
        ? Number(apiRoundRecord.grading_weight)
        : null;
      if (storedGW === null || Math.abs(storedGW - editedGWNum) > 1e-9) {
        gradingWeightPayload = editedGWNum;
      }
    }

    // Determine which API to call based on source
    const apiRound = rounds.find(r => r.id === round_id && r.source === 'round_api');

    if (apiRound) {
      // Round exists in general-info → PATCH to round API with only the
      // fields the instructor actually changed. Using PATCH avoids
      // re-sending an already-in-the-past start_date when only end_date
      // changed, which would otherwise trip the backend's
      // "start date cannot be less than now" validation.
      const roundEdits = this.state.editedDates[round_id] || {};
      const patchBody = {};
      if (roundEdits.start_date) patchBody.start_date = startDate.toISOString();
      if (roundEdits.end_date) patchBody.end_date = endDate.toISOString();
      if (gradingWeightPayload !== undefined) patchBody.grading_weight = gradingWeightPayload;

      patchRequest(`${apiUrl}game/rounds/${round_id}/`)
        .send(patchBody)
        .then(res => {
          const nextRounds = this.state.rounds.map(r => {
            if (r.id !== round_id) return r;
            const updated = { ...r };
            if (patchBody.start_date) updated.start_date = startDate;
            if (patchBody.end_date) updated.end_date = endDate;
            if (patchBody.grading_weight !== undefined) updated.grading_weight = patchBody.grading_weight;
            return updated;
          });
          this.setState({
            loadingButtonRound: 0,
            rounds: nextRounds,
            changedRounds: changedRounds.filter(i => i !== round_id),
            editedDates: Object.keys(this.state.editedDates).reduce((acc, key) => { if (key !== String(round_id)) acc[key] = this.state.editedDates[key]; return acc; }, {}),
            showSuccess: true,
            successfullyChangedRoundNumber: round.displayName || `Round ${round.number}`,
          });
          // Rebuild round-admin state from BE to defend against any
          // divergence between the local in-memory update above and
          // the persisted server state. Instructors were seeing empty
          // date inputs right after a successful save and only a
          // manual refresh recovered — this refetch removes the need
          // for that refresh.
          clearAppGeneralInfoCache();
          this.fetchData();
        })
        .catch(err => {
          this.handleUpdateError(err);
        });
      return;
    }

    if (isPractice) {
      // Explorer/Adventurer practice round: always uses the round API.
      // The scheduler is not involved — setting practice dates auto-opens
      // the practice round.
      postRequest(`${apiUrl}game/rounds/`)
        .send({
          number: 1,
          type: 1,
          practice: 'Yes',
          results_viewable: true,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          game: gameId,
          ...(gradingWeightPayload !== undefined ? { grading_weight: gradingWeightPayload } : {}),
        })
        .then(res => {
          const newRound = {
            id: res.body.id,
            number: 1,
            start_date: startDate,
            end_date: endDate,
            showResults: true,
            is_started: false,
            is_finished: false,
            practice: 'Yes',
            source: 'round_api',
            grading_weight: gradingWeightPayload,
          };
          const nextRounds = [...rounds, newRound];
          this.setState({
            loadingButtonRound: 0,
            changedRounds: changedRounds.filter(i => i !== round_id),
            editedDates: Object.keys(this.state.editedDates).reduce((acc, key) => { if (key !== String(round_id)) acc[key] = this.state.editedDates[key]; return acc; }, {}),
            rounds: nextRounds,
            showSuccess: true,
            successfullyChangedRoundNumber: round.displayName || 'Practice',
          }, () => {
            this.notifyRoundCreated({
              gameType,
              gameReset,
              isPractice: true,
              createdRoundNumber: 1,
              newRounds: nextRounds,
              newSchedulers: schedulers,
            });
            // See PATCH branch above for why we refetch here.
            clearAppGeneralInfoCache();
            this.fetchData();
          });
        })
        .catch(err => {
          this.handleUpdateError(err);
        });
      return;
    }

    if (isLegend || gameReset) {
      // Legend has no scheduler at all. Explorer/Adventurer main rounds
      // also use the round API once the game has been reset (after
      // "Activate Main Rounds" was clicked). Pre-reset Explorer/Adventurer
      // main rounds fall through to the scheduler paths below.
      postRequest(`${apiUrl}game/rounds/`)
        .send({
          number: round.number,
          type: 2,
          practice: 'No',
          results_viewable: true,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          game: gameId,
          ...(gradingWeightPayload !== undefined ? { grading_weight: gradingWeightPayload } : {}),
        })
        .then(res => {
          const newRound = {
            id: res.body.id,
            number: round.number,
            start_date: startDate,
            end_date: endDate,
            showResults: true,
            is_started: false,
            is_finished: false,
            practice: 'No',
            source: 'round_api',
            grading_weight: gradingWeightPayload,
          };
          const nextRounds = [...rounds, newRound];
          this.setState({
            loadingButtonRound: 0,
            changedRounds: changedRounds.filter(i => i !== round_id),
            editedDates: Object.keys(this.state.editedDates).reduce((acc, key) => { if (key !== String(round_id)) acc[key] = this.state.editedDates[key]; return acc; }, {}),
            rounds: nextRounds,
            showSuccess: true,
            successfullyChangedRoundNumber: round.displayName || `Round ${round.number}`,
          }, () => {
            this.notifyRoundCreated({
              gameType,
              gameReset,
              isPractice: false,
              createdRoundNumber: round.number,
              newRounds: nextRounds,
              newSchedulers: schedulers,
            });
            // See PATCH branch above for why we refetch here.
            clearAppGeneralInfoCache();
            this.fetchData();
          });
        })
        .catch(err => {
          this.handleUpdateError(err);
        });
      return;
    }

    // Explorer/Adventurer main round, pre-reset: use scheduler API
    const scheduler = schedulers.find(s => s.id === round_id && s.source === 'scheduler');

    if (scheduler) {
      // Scheduler exists → PUT to scheduler API
      const { courseId: schedCourseId } = this.state;
      putRequest(`${apiUrl}v2/games/roundschedulers/${scheduler.scheduler_id || scheduler.id}/`)
        .send({
          round_number: round.number,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          course: schedCourseId,
          practice: false,
          ...(gradingWeightPayload !== undefined ? { grading_weight: gradingWeightPayload } : {}),
        })
        .then(res => {
          const nextSchedulers = this.state.schedulers.map(s => {
            if (s.id !== round_id) return s;
            const updated = { ...s, start_date: startDate, end_date: endDate };
            if (gradingWeightPayload !== undefined) updated.grading_weight = gradingWeightPayload;
            return updated;
          });
          this.setState({
            loadingButtonRound: 0,
            schedulers: nextSchedulers,
            changedRounds: changedRounds.filter(i => i !== round_id),
            editedDates: Object.keys(this.state.editedDates).reduce((acc, key) => { if (key !== String(round_id)) acc[key] = this.state.editedDates[key]; return acc; }, {}),
            showSuccess: true,
            successfullyChangedRoundNumber: round.displayName || `Round ${round.number}`,
          });
          // See PATCH branch above for why we refetch here.
          clearAppGeneralInfoCache();
          this.fetchData();
        })
        .catch(err => {
          this.handleUpdateError(err);
        });
      return;
    }

    // Neither exists → POST to scheduler API
    const { courseId } = this.state;
    postRequest(`${apiUrl}v2/games/roundschedulers/`)
      .send({
        round_number: round.number,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        course: courseId,
        practice: false,
        ...(gradingWeightPayload !== undefined ? { grading_weight: gradingWeightPayload } : {}),
      })
      .then(res => {
        // Add the new scheduler to state
        const newScheduler = {
          id: res.body.id,
          number: round.number,
          start_date: startDate,
          end_date: endDate,
          showResults: false,
          is_started: false,
          is_finished: false,
          practice: false,
          source: 'scheduler',
          scheduler_id: res.body.id,
          grading_weight: gradingWeightPayload,
        };

        const nextSchedulers = [...schedulers, newScheduler];
        this.setState({
          loadingButtonRound: 0,
          changedRounds: changedRounds.filter(i => i !== round_id),
          schedulers: nextSchedulers,
          showSuccess: true,
          successfullyChangedRoundNumber: round.displayName || `Round ${round.number}`,
        }, () => {
          this.notifyRoundCreated({
            gameType,
            gameReset,
            isPractice: false,
            createdRoundNumber: round.number,
            newRounds: rounds,
            newSchedulers: nextSchedulers,
          });
          // See PATCH branch above for why we refetch here.
          clearAppGeneralInfoCache();
          this.fetchData();
        });
      })
      .catch(err => {
        this.handleUpdateError(err);
      });
  };

  handleUpdateError = (err) => {
    this.setState({ loadingButtonRound: 0 });

    const message = extractRoundErrorMessage(err)
      || "Unable to update the round. Please try again.";

    this.setState({
      showError: true,
      errorTitle: 'Error | Server Error',
      errorMessage: message,
    });
  }

  onShowResultsChange = round_id => {
    const { rounds } = this.state;
    const round = rounds.find(i => i.id === round_id);
    if (!round) return;

    const newShowResults = !round.showResults;

    const url = `${process.env.REACT_APP_API_URL}game/rounds/${round_id}/`;
    const request = patchRequest(url);

    // Only the one field that's actually changing. The backend's PATCH
    // semantics handle partial updates — sending start/end dates again
    // is redundant.
    request.send({ results_viewable: !round.showResults })
      .then(() => {
        const updatedRounds = rounds.map(r =>
          r.id === round_id ? { ...r, showResults: newShowResults } : r
        );
        this.setState({ rounds: updatedRounds });
      })
      .catch(err => {
        this.handleUpdateError(err);
      });
  };

  hideSuccessAlert = () => {
    this.setState({ showSuccess: false });
  };

  hideErrorAlert = () => {
    this.setState({ showError: false });
  };

  onActivationClick = () => {
    const activationState = this.getActivationButtonState();
    if (!activationState || !activationState.isClickable) return;

    if (activationState.type === 'start_main') {
      this.handleStartMainRounds();
    }
  }

  // "Activate Main Rounds" click handler
  handleStartMainRounds = () => {
    const { gameType, schedulers } = this.state;

    // Block activation when Round 1's scheduled end has already
    // passed. In that case the saved start/end are both in the
    // past and there's no sensible start time to substitute — the
    // instructor needs to edit the deadlines first.
    const round1 = schedulers.find(
      s => Number(s.number) === 1 && s.practice !== true
    );
    if (round1 && round1.end_date && moment().isAfter(round1.end_date)) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Round Deadlines in the Past',
        errorMessage: "Round 1's end date is in the past. Please adjust the round deadlines so they are not in the past before activating.",
      });
      return;
    }

    if (gameType === 'Legend') {
      // Legend: no practice round, skip confirmation
      this.proceedWithMainRounds();
    } else {
      // Explorer or Adventurer: show confirmation modal
      this.setState({ showConfirmModal: true });
    }
  }

  onConfirmMainRounds = () => {
    this.setState({ showConfirmModal: false });
    this.proceedWithMainRounds();
  }

  // Step 2/3: Entry point after confirmation
  proceedWithMainRounds = () => {
    const { gameReset } = this.state;

    this.setState({ activationLoading: true });

    if (!gameReset) {
      // Step 4: Reset game first
      this.resetGame(0);
    } else {
      // reset=true already: verify that main rounds don't already
      // exist on the backend before jumping to createMainRounds. If
      // any exist, we're in a stale state (e.g. reset_game failed
      // previously but game.reset was flipped to true manually) and
      // we must re-run reset_game to clear them.
      this.verifyAndCreateMainRounds();
    }
  }

  // Shared error copy for any reset_game failure. Named so it stays
  // consistent across the initial-reset and stale-state paths.
  showResetFailedError = () => {
    this.setState({
      activationLoading: false,
      showError: true,
      errorTitle: 'Error | Reset Failed',
      errorMessage: "Game reset failed. Please try a few more times. If it continues to fail, reach out to support@marketgames.io.",
    });
  }

  // Handles the "gameReset is already true" branch: if any main
  // rounds (practice: 'No') already exist for this game, re-run
  // reset_game to wipe them. On success proceed to createMainRounds;
  // on failure revert game.reset to false on the backend so the FE
  // and BE don't stay out of sync, then surface the error.
  //
  // Uses a FRESH game/rounds/ fetch (not state.rounds) so the check
  // is accurate even if state.rounds was loaded a while ago. This is
  // the safer default given how consequential creating rounds is.
  verifyAndCreateMainRounds = () => {
    const { gameId } = this.state;
    const apiUrl = process.env.REACT_APP_API_URL;

    getRequest(`${apiUrl}game/rounds/`)
      .then(res => {
        const body = (res && res.body) || {};
        const rows = Array.isArray(body) ? body : (body.results || []);
        const mainRoundsExist = rows.some(
          r => r && Number(r.game) === Number(gameId) && r.practice === 'No'
        );

        if (!mainRoundsExist) {
          // Clean state — proceed straight to Step 6.
          this.createMainRounds();
          return;
        }

        // Stale state — re-run reset_game with revert-on-failure.
        this.resetGameForStaleState(0);
      })
      .catch(() => {
        // If we can't confirm the round state, treat it like a reset
        // failure — safer than blindly creating rounds on top of a
        // possibly dirty backend.
        this.showResetFailedError();
      });
  }

  // Reset variant used when we KNOW game.reset was already true but
  // rounds are still present. On failure, POST game.reset = false to
  // undo the stale flag so the next attempt starts from a consistent
  // baseline (rather than repeatedly skipping reset).
  resetGameForStaleState = (retryCount) => {
    const { gameId } = this.state;
    const apiUrl = process.env.REACT_APP_API_URL;

    postRequest(`${apiUrl}games/reset_game/`)
      .send({ game: gameId })
      .then(() => {
        // reset_game itself succeeded — make sure game.reset stays
        // true on the backend (it already was) and proceed.
        this.createMainRounds();
      })
      .catch(err => {
        const status = err && err.status;
        const isTransient = !status || status >= 500;
        if (isTransient && retryCount < 2) {
          setTimeout(() => this.resetGameForStaleState(retryCount + 1), 2000);
          return;
        }
        // Revert game.reset to false so state matches "reset didn't
        // happen" for the next click. Fire-and-forget: if this also
        // fails, the FE-side state was already reset, and the error
        // message tells the instructor to retry.
        //
        // PATCH because we're touching a single field on an existing
        // record. The endpoint doesn't accept POST — DRF ModelViewSet
        // detail routes are GET/PUT/PATCH/DELETE only.
        patchRequest(`${apiUrl}games/${gameId}/`)
          .send({ reset: false })
          .then(() => {
            this.setState({ gameReset: false });
          })
          .catch(() => { /* silent */ });
        this.showResetFailedError();
      });
  }

  // Step 4: Reset game (up to 3 attempts on transient failures)
  resetGame = (retryCount) => {
    const { gameId } = this.state;
    const apiUrl = process.env.REACT_APP_API_URL;

    postRequest(`${apiUrl}games/reset_game/`)
      .send({ game: gameId })
      .then(res => {
        // Step 5: Update game reset = true. PATCH because we're
        // touching a single field on an existing record; the detail
        // route doesn't accept POST.
        //
        // Failure here is NOT fatal to activation — the actual reset
        // (Step 4) already succeeded on the backend and the
        // schedulers already have the data we need for Step 6. Log
        // the error, optimistically flip local gameReset so state
        // reflects "the reset happened," and continue to
        // createMainRounds. The next fetchData will reconcile if
        // game.reset stayed false server-side.
        patchRequest(`${apiUrl}games/${gameId}/`)
          .send({ reset: true })
          .then(() => {
            this.setState({ gameReset: true });
            this.createMainRounds();
          })
          .catch(err => {
            // eslint-disable-next-line no-console
            console.error('RoundAdmin: failed to PATCH game.reset=true; proceeding anyway', err);
            this.setState({ gameReset: true });
            this.createMainRounds();
          });
      })
      .catch(err => {
        // Only retry transient failures (5xx or no response). A 4xx
        // means the request is wrong (bad payload, endpoint gone,
        // auth) and repeating won't help.
        const status = err && err.status;
        const isTransient = !status || status >= 500;
        if (isTransient && retryCount < 2) {
          setTimeout(() => this.resetGame(retryCount + 1), 2000);
        } else {
          this.showResetFailedError();
        }
      });
  }

  // Step 6: Create main rounds using scheduler data, then Step 7: Rotate roles
  createMainRounds = () => {
    const { schedulers, gameType, gameId } = this.state;
    const apiUrl = process.env.REACT_APP_API_URL;
    const requiredRounds = gameType === 'Explorer' ? [1] : [1, 2, 3];

    // Guard: every required round must have a scheduler row before we
    // start POSTing. Otherwise a missing scheduler causes a null
    // dereference on scheduler.start_date and either crashes silently
    // or ships a bad request. Surface a clear error instead.
    const missing = requiredRounds.filter(
      n => !schedulers.some(s => Number(s.number) === Number(n))
    );
    if (missing.length > 0) {
      this.setState({
        activationLoading: false,
        showError: true,
        errorTitle: 'Error | Missing Round Deadlines',
        errorMessage: `Set start and end dates for Round ${missing.join(', ')} before activating the main rounds.`,
      });
      return;
    }

    const createRound = (index) => {
      if (index >= requiredRounds.length) {
        // All rounds created, proceed to Step 7: Rotate roles
        this.rotateRoles();
        return;
      }

      const roundNumber = requiredRounds[index];
      const scheduler = schedulers.find(s => Number(s.number) === Number(roundNumber));

      // Round 1 start-time safeguard: when the instructor finally
      // activates the main rounds after the scheduled start has
      // already passed, use "now" instead of the stale scheduled
      // start so the round reflects the actual go-live moment. The
      // end-date-in-past case is caught upstream in
      // handleStartMainRounds and never reaches here. Later rounds
      // (2, 3) always ship their scheduled start_date verbatim.
      const scheduledStart = moment(scheduler.start_date);
      const startDate = (roundNumber === 1 && moment().isAfter(scheduledStart))
        ? moment()
        : scheduledStart;

      // Carry the scheduler's grading_weight (if any) onto the newly
      // created round API record. Without this, the Round Admin table
      // would snap back to the 0.3333 default under the gameReset=true
      // round-API-only rule, silently wiping any custom weight the
      // instructor set before activating.
      const schedulerGW = scheduler && scheduler.grading_weight != null
        ? Number(scheduler.grading_weight)
        : null;
      const roundBody = {
        number: roundNumber,
        type: 2,
        practice: 'No',
        results_viewable: true,
        start_date: startDate.toISOString(),
        end_date: moment(scheduler.end_date).toISOString(),
        game: gameId,
      };
      if (schedulerGW != null && Number.isFinite(schedulerGW)) {
        roundBody.grading_weight = schedulerGW;
      }

      postRequest(`${apiUrl}game/rounds/`)
        .send(roundBody)
        .then(res => {
          createRound(index + 1);
        })
        .catch(err => {
          this.setState({
            activationLoading: false,
            showError: true,
            errorTitle: 'Error | Round Creation Failed',
            errorMessage: extractRoundErrorMessage(err)
              || `Unable to create Round ${roundNumber}. Please try again.`,
          });
        });
    };

    createRound(0);
  }

  // Step 7: Rotate roles (if success or fail, finish)
  rotateRoles = () => {
    const { gameId } = this.state;
    const apiUrl = process.env.REACT_APP_API_URL;

    // The activation is considered successful once rounds are created
    // (Step 6). Role rotation is best-effort; fire the confirmation
    // popup in both branches. The popup replaces the old "Main Rounds
    // Activated!" persistent button — user now gets a one-time
    // confirmation and the button simply disappears on next render.
    const finish = () => {
      this.setState({ activationLoading: false });
      this.openRoundsSetPopup(
        'Main rounds are live!',
        'Your main rounds are set. Students can start posting decisions for Round 1.'
      );
      // The activation flow PATCHed game.reset=true on the backend.
      // fetchAppGeneralInfo has a 5-minute cache, so a naked refetch
      // here would return the pre-activation payload (game.reset=false)
      // and flip state.gameReset back to false — which brings the
      // Activate Main Rounds button back even though activation
      // succeeded. Bust the cache first so the refetch hits the wire.
      clearAppGeneralInfoCache();
      this.fetchData();
    };

    postRequest(`${apiUrl}v2/games/rotate-roles/`)
      .send({ game_id: gameId })
      .then(finish)
      .catch(finish);
  }

  getActivationButtonState = () => {
    const { gameReset, gameType, rounds } = this.state;

    // Legend has no practice round and doesn't use the scheduler-based
    // activation flow — its rounds are created directly via the round API
    // when the instructor saves dates, so no activation button is shown.
    if (gameType === 'Legend') {
      return null;
    }

    // Post-activation (gameReset === true): the button disappears
    // entirely. Confirmation is delivered as a one-time popup from
    // rotateRoles instead of a persistent "Main Rounds Activated!"
    // pill sitting on the page forever.
    if (gameReset) {
      return null;
    }

    // Explorer/Adventurer, pre-activation: the button is gated on the
    // practice round having ended. Practice round is identified by
    // `practice === 'Yes'` in the round API; the button becomes active
    // once its end_date is in the past.
    if (!gameReset) {
      const practiceRoundEnded = rounds.some(r =>
        r.practice === 'Yes' && r.end_date && moment(r.end_date).isBefore(moment())
      );

      if (practiceRoundEnded) {
        return { prefix: 'Activate', status: 'Main Rounds', isClickable: true, type: 'start_main' };
      }

      return {
        prefix: 'Activate',
        status: 'Main Rounds',
        isClickable: false,
        type: 'activate_main_inactive',
        tooltip: 'You can activate the main rounds once the practice round ends.',
      };
    }

    return null;
  }

  render() {
    const {
      loaded,
      changedRounds,
      showSuccess,
      successfullyChangedRoundNumber,
      showError,
      errorTitle,
      errorMessage,
      loadingButtonRound,
      defaultActiveRounds,
      showConfirmModal,
      activationLoading,
    } = this.state;

    const activationState = loaded ? this.getActivationButtonState() : null;
    const isIAS = Auth.isAssistantOrCoInstructor();
    const cardSubtitle = this.state.gameType === 'Legend'
      ? 'Set the round deadlines.'
      : 'Set the round deadlines. Once the practice round ends, click Activate to start the main rounds.';

    return (
      <Wrapper>
        <PageTitle>Round Admin</PageTitle>
        {showError && (
          <ErrorAlert
            title={errorTitle}
            message={errorMessage}
            onHide={this.hideErrorAlert}
          />
        )}
        {showSuccess && (
          <DecisionsAlert
            type='success'
            title='Success!'
            message={`${successfullyChangedRoundNumber} has been successfully updated!`}
            onHide={this.hideSuccessAlert}
          />
        )}

        <CardWrapper>
          <CardTitle>Round Deadlines</CardTitle>
          <CardSubtitle>{cardSubtitle}</CardSubtitle>
          <CardDivider />
          {
            !loaded &&
            <LoaderWrapper>
              <Loader type="ball-scale-ripple" active />
            </LoaderWrapper>
          }
          {
            loaded &&
            <RoundAdminTable
              rounds={this.getDisplayRounds()}
              onChange={this.onChange}
              onGradingWeightChange={this.onGradingWeightChange}
              changedRounds={changedRounds}
              onUpdate={this.onUpdate}
              loadingButtonRound={loadingButtonRound}
              showOverlay={this.showOverlay}
              hideOverlay={this.hideOverlay}
              defaultActiveRounds={defaultActiveRounds}
              onShowResultsChange={this.onShowResultsChange}
              isIAS={isIAS}
            />
          }
          {loaded && activationState && !isIAS && (
            <CardActivationWrapper>
              <ActivationHoverGroup>
                <ActivationButton
                  isClickable={activationState.isClickable && !activationLoading}
                  onClick={this.onActivationClick}
                >
                  {activationLoading ? (
                    <Loader type="ball-scale-ripple" active />
                  ) : (
                    <ActivationButtonText>
                      <span className="prefix">{activationState.prefix}</span>
                      <span className="status">{activationState.status}</span>
                    </ActivationButtonText>
                  )}
                </ActivationButton>
                {activationState.tooltip && (
                  <ActivationTooltip>{activationState.tooltip}</ActivationTooltip>
                )}
              </ActivationHoverGroup>
            </CardActivationWrapper>
          )}
        </CardWrapper>

        {showConfirmModal && (
          <ConfirmOverlay onClick={() => this.setState({ showConfirmModal: false })}>
            <ConfirmModal onClick={e => e.stopPropagation()}>
              <ConfirmTitle>Are You Sure?</ConfirmTitle>
              <ConfirmMessage>
                Are you sure you're ready to begin the Main Rounds? Starting the Main Rounds will reset the game and permanently clear all Practice Round data. This action cannot be undone.
              </ConfirmMessage>
              <ConfirmActions>
                <ConfirmCancelButton onClick={() => this.setState({ showConfirmModal: false })}>
                  Cancel
                </ConfirmCancelButton>
                <ConfirmProceedButton onClick={this.onConfirmMainRounds}>
                  I'm sure
                </ConfirmProceedButton>
              </ConfirmActions>
            </ConfirmModal>
          </ConfirmOverlay>
        )}

        <FirstDecisionModal
          open={this.state.roundsSetPopup.open}
          title={this.state.roundsSetPopup.title}
          body={this.state.roundsSetPopup.body}
          ctaLabel="Got it"
          onCta={this.closeRoundsSetPopup}
          onClose={this.closeRoundsSetPopup}
        />
      </Wrapper>
    );
  }
}

export default RoundAdmin;



// WEBPACK FOOTER //
// ./src/instructor/containers/round-admin.js